/**
 * Local cache lifecycle: which Supabase user owns the synced localStorage cache,
 * and the routines that clear it on logout / account switch / account deletion.
 *
 * Data-safety rules this enforces:
 *   - A logout clears the user-scoped DATA but never mirrors that clear to the
 *     cloud (all clears use the UNWRAPPED rawRemoveItem — a wrapped removeItem
 *     while the user id is still set would push an empty array up and wipe the
 *     user's cloud rows).
 *   - An account switch (a cache owned by a DIFFERENT user than the one now
 *     logging in) discards the ENTIRE synced cache before reconcile, so nothing
 *     from the prior user is shown or seeded upward.
 *
 * The owner tag lives OUTSIDE SYNC_KEYS on purpose, so a logout data-clear leaves
 * it in place — we still need it to detect a later account switch.
 */
import { SYNC_KEYS } from './syncKeys';
import { rawRemoveItem } from './writeThrough';

/** localStorage key marking which Supabase user the current synced cache belongs to. */
const CACHE_OWNER_KEY = 'mycryptoplanner_cache_owner';

/**
 * Display / device preferences that MAY stay on a shared device across an explicit
 * logout. Every one is per-user in the cloud, so reconcile restores each user's
 * own value on their next login; they're still cleared on an account SWITCH (see
 * clearAllSyncedLocal) so an incoming user never inherits or re-seeds them.
 */
const LOGOUT_PRESERVE_KEYS = new Set<string>([
  'mycryptoplanner_theme',
  'mycryptoplanner_currency',
  'mycryptoplanner_chart_view',
  'sampleDataDismissed',
]);

const ALL_SYNCED_KEYS = Object.keys(SYNC_KEYS);

// ── Owner tag ────────────────────────────────────────────────────────────────

/** The user id that owns the current localStorage cache, or null if unstamped. */
export function getCacheOwner(): string | null {
  try {
    return window.localStorage.getItem(CACHE_OWNER_KEY);
  } catch {
    return null;
  }
}

/** Stamp the current cache as owned by `userId` (CACHE_OWNER_KEY isn't a synced
 *  key, so this never triggers a cloud push). */
export function setCacheOwner(userId: string): void {
  try {
    window.localStorage.setItem(CACHE_OWNER_KEY, userId);
  } catch {
    /* ignore storage failures */
  }
}

// ── Clears ───────────────────────────────────────────────────────────────────

/**
 * Clear the user-scoped DATA cached in localStorage on explicit logout, keeping
 * the display prefs in LOGOUT_PRESERVE_KEYS. Uses the UNWRAPPED remover so the
 * clear is never mirrored to the cloud. The owner tag is intentionally left in
 * place so a later login by a different user is detected as a switch.
 *
 * DELIBERATE TRADE-OFF: edits made while fully offline are pushed to the cloud by
 * the write-through as soon as connectivity returns, but if the user instead logs
 * out while still offline, those never-synced edits are cleared here and lost.
 * That is an accepted cost of scoping the cache to its owner: keeping the data
 * would mean either leaving one user's rows on a shared device or seeding them
 * into whoever logs in next (the exact cross-account leak this change fixes).
 */
export function clearSyncedUserDataOnLogout(): void {
  for (const key of ALL_SYNCED_KEYS) {
    if (LOGOUT_PRESERVE_KEYS.has(key)) continue;
    rawRemoveItem(key);
  }
}

/**
 * Discard the ENTIRE synced cache (data + display prefs) — used when the cache
 * belongs to a different user than the one now logging in, so nothing from the
 * prior user survives to be displayed or seeded upward. Unwrapped, so no cloud push.
 */
export function clearAllSyncedLocal(): void {
  for (const key of ALL_SYNCED_KEYS) rawRemoveItem(key);
}

/**
 * FULL local wipe for account deletion: every mycryptoplanner_* key plus the app
 * keys stored without that prefix. (Extracted from SettingsPage so the key set
 * lives in one place.) The account is gone server-side, so mirroring here is moot.
 */
export function clearAllLocalAppData(): void {
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith('mycryptoplanner_')) toRemove.push(k);
    }
    toRemove.forEach((k) => window.localStorage.removeItem(k));
    // App keys that aren't under the mycryptoplanner_ prefix.
    ['cashOutGoal', 'goalShowProjection', 'cgtBracketIdx', 'sampleDataDismissed', 'taxPlanEstimates'].forEach(
      (k) => window.localStorage.removeItem(k)
    );
  } catch {
    /* ignore */
  }
}
